import { Destination } from '../../services/DestinationsApiClient';
import { OutputInfo } from '../../types/obs';
import { promisefy } from '../../utils/promisefy';
import { queryOptions } from '@tanstack/react-query';

const knownOutputServerNamesRegex = /(youtube|tiktok|trovo|twitch|\/(x))/;

export function defaultOutputIsSetUp(output: OutputInfo) {
  return [!!output.streamingSettings?.serverUrl, !!output.streamingSettings?.streamKey].every(Boolean);
}

export function defaultOutputIsEqualToTemplate({
  defaultOutput,
  template,
}: {
  defaultOutput: OutputInfo;
  template: Destination['outputs'][number] | undefined;
}) {
  if (!template) {
    return false;
  }

  return [
    defaultOutputIsSetUp(defaultOutput),
    defaultOutput.streamingSettings?.serverUrl === template.streamingSettings?.serverUrl,
    defaultOutput.streamingSettings?.streamKey === template.streamingSettings?.streamKey,
  ].every(Boolean);
}

export async function setupDefaultToPrimaryOutput({
  defaultOutput,
  templateOutput,
  primaryOutput,
  forceUpdateToPrimaryOutput,
}: {
  forceUpdateToPrimaryOutput: boolean;
  defaultOutput: OutputInfo;
  primaryOutput: Destination['outputs'][number] | undefined;
  templateOutput: Destination['outputs'][number] | undefined;
}) {
  const defaultIsSetUp = defaultOutputIsSetUp(defaultOutput);
  const isDefaultEqualToPrimary = templateOutput
    ? defaultOutputIsEqualToTemplate({ defaultOutput, template: templateOutput })
    : false;

  if (defaultIsSetUp && !forceUpdateToPrimaryOutput) {
    console.log('Default output is already set up, skipping');
    return { didUpdate: false, isDefaultEqualToPrimary, defaultIsSetUp };
  }

  // if (isDefaultEqualToPrimary) {
  //   console.log('Default output is already equal to primary output, skipping');
  //   console.log('Setting default output to primary output');
  //   await promisefy(window.host.setStreamingSettings)(primaryOutput.streamingSettings);
  //   return { didUpdate: true, isDefaultEqualToPrimary, defaultIsSetUp };
  // }

  if (!primaryOutput) {
    console.log('No primary output found, skipping');
    return { didUpdate: false, isDefaultEqualToPrimary, defaultIsSetUp };
  }

  console.log('Setting default output to primary output');
  await promisefy(window.host.setStreamingSettings)(primaryOutput.streamingSettings);

  return { didUpdate: true, isDefaultEqualToPrimary: true, defaultIsSetUp: true };
}

export async function getDefaultCanvasConfigurationStatus() {
  const { default: defaultOutput, ...outputs } = await promisefy(window.host.getAllStreamingOutputs)();

  if (!defaultOutput) {
    return {
      isConfigured: true, // Dont want to setup default canvas configuration if there is no default output
      isConfiguredFromCustomOutput: true,
      outputName: 'Unknown',
    };
  }

  const outputsList = Object.values(outputs);

  if (!defaultOutput?.streamingSettings.serverUrl || !defaultOutput?.streamingSettings.streamKey) {
    return {
      isConfigured: false,
      isConfiguredFromCustomOutput: false,
      outputName: 'Unknown',
    };
  }

  let streamSettings = defaultOutput.streamingSettings;

  const result = {
    isConfigured: true,
    isConfiguredFromCustomOutput: false,
    outputName: 'Unknown',
  };

  const match = knownOutputServerNamesRegex.exec(streamSettings.serverUrl);

  if (match) {
    const [outputName] = match;
    result.outputName = match.pop() ?? outputName;
  } else if (streamSettings?.streamKey?.startsWith('live_')) {
    result.outputName = 'twitch';
  }

  // IF OBS Streaming Setting is configured to an enabled output to the default canvas: do nothing.
  if (
    outputsList.some((output) => {
      if (!(output.auxiliaryData as { isPrimaryOutput: boolean })?.isPrimaryOutput) {
        return false;
      }

      const { streamKey } = output.streamingSettings;
      const isSame = streamKey === streamSettings.streamKey;

      if (isSame) {
        streamSettings = output.streamingSettings;
      }

      return isSame;
    })
  ) {
    result.isConfiguredFromCustomOutput = true;
  }

  return result;
}

/**
 * Setup the default canvas streaming settings.
 * The function assumes that all canvases and custom outputs are already registered.
 */
export async function getTopmostConfiguredOutput() {
  const { default: defaultOutput, ...outputs } = await promisefy(window.host.getAllStreamingOutputs)();

  void defaultOutput;

  const outputsList = Object.values(outputs).filter((output) => {
    const o = output as Destination['outputs'][number];
    const { isEnabled } = o;
    return isEnabled;
  }) as Destination['outputs'][number][];

  let topmostOutput: Destination['outputs'][number] | undefined;

  for (const output of outputsList) {
    if (!output.videoCompositionId || output.videoCompositionId === 'default') {
      topmostOutput = output;
      break;
    }
  }

  return topmostOutput;
}

export const defaultCanvasConfigurationStatusQueryOptions = queryOptions({
  queryKey: ['defaultCanvasConfigurationStatus'],
  queryFn: getDefaultCanvasConfigurationStatus,
  refetchInterval: 3 * 1000,
});
